/// <amd-module name="Environments/Providers/VirtualMachineGroups/Components/AddTarget" />
import * as TargetUtils from "Environments/Providers/VirtualMachineGroups/Common/TargetUtils"
import * as Resources from "Environments/Providers/VirtualMachineGroups/Resources";
import * as React from "react";
import * as StringUtils from "VSS/Core/Util/String";

import { PackageMetadata, TaskAgent, TaskAgentPool, VirtualMachineGroup } from "DistributedTask/Client/Contracts/TaskAgent";
import { PageContextHelper } from "Environments/Client/PageContextHelper"
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getErrorMessage } from "Environments/EnvironmentsCommon/Utils"
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { VirtualMachineGroupActionCreator } from "Environments/Providers/VirtualMachineGroups/ActionsCreators/VirtualMachineGroupActionCreator";
import { Operation } from "Environments/Providers/VirtualMachineGroups/Common/Utils"
import { VirtualMachineGroupStore } from "Environments/Providers/VirtualMachineGroups/Stores/VirtualMachineGroupStore";
import { getTitle } from "Environments/Providers/VirtualMachineGroups/Utilities";
import { PageKey } from "Environments/Providers/VirtualMachineGroups/Utilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Page } from "VSSUI/Page";
import { Panel } from "VSSUI/Panel";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import "VSS/LoaderPlugins/Css!Environments/Providers/VirtualMachineGroups/components/VirtualMachineGroupTargets";

interface IAddTargetState extends IEnvironmentBaseComponentState {
    accessToken: string;
    isDotnetCoreV2Agent: boolean;
    packages: {
        [key: string]: PackageMetadata
    };
    selectedPlatform: string;
    taskAgentPool: TaskAgentPool | undefined;
    toolTipMessage: string;
    usePATToken: boolean;
    errorMessage?: string;
    showWarning?: boolean;
}

interface IAddTargetProps extends IEnvironmentBaseComponentProperties {
    instanceId: string;
    onDismissPanel: () => void;
    virtualMachineGroup: VirtualMachineGroup;
}

export class AddTarget extends EnvironmentBaseComponent<IAddTargetProps, IAddTargetState>{

    private _vmActionCreator: VirtualMachineGroupActionCreator;
    private _vmStore: VirtualMachineGroupStore;
    private _osSelection = new DropdownSelection();

    constructor(props: IAddTargetProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getState();

        this._vmActionCreator = ActionCreatorManager.CreateActionCreator(VirtualMachineGroupActionCreator, this.props.instanceId);
        this._vmStore = StoreManager.CreateStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.props.instanceId);


        this._vmStore.addListener(Operation.GetTargetPackages, this._onGetTargetPackagesOperation);
        this._vmStore.addListener(Operation.GetTaskAgentPool, this._onGetTaskAgentPoolOperation);
        this._vmStore.addListener(Operation.GetPATToken, this._onGetPATTokenOperation);

        this._vmActionCreator.getTargetPackages(this.context.pageContext);
        this._vmActionCreator.getTaskAgentPool(this.context.pageContext, this.props.virtualMachineGroup.poolId);

        this._osSelection.select(0);
    }

    public getInstanceId(context: IVssComponentContext): string {
        return this.props.instanceId;
    }

    public componentWillUnmount(): void {
        this._vmStore.removeListener(Operation.GetTargetPackages, this._onGetTargetPackagesOperation);
        this._vmStore.removeListener(Operation.GetTaskAgentPool, this._onGetTaskAgentPoolOperation);
        this._vmStore.removeListener(Operation.GetPATToken, this._onGetPATTokenOperation);

        StoreManager.DeleteStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.props.instanceId);
        ActionCreatorManager.DeleteActionCreator<VirtualMachineGroupActionCreator>(VirtualMachineGroupActionCreator, this.props.in);
    }


    public render(): JSX.Element {
        return (
            <Panel titleProps={getTitle((!!this.props.virtualMachineGroup) ? this.props.virtualMachineGroup.name : "")} onDismiss={this.props.onDismissPanel}>
                {this._getAddTargetGuidance()}
            </Panel>
        )
    }

    private _getAddTargetGuidance(): JSX.Element {

        const isHosted = PageContextHelper.isHostedType(this.context.pageContext);
        const copyLinkTitle = StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows) || StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows_7) ? Resources.CopyPSScript : Resources.CopyRegistrationScript;

        return (
            <div className={"page-content-top add-machine-guidance"} role="region" aria-label={Resources.RegisterMachine} >
                {this.getErrorMessageView(PageKey.AddTargetView)}
                <h2 className="title-xs add-machine-title">{Resources.VirtualMachineResources}</h2>
                <FormItem className="add-machine-scripts-platforms" label={Resources.OperatingSystem}>
                    <Dropdown
                        ariaLabel={Resources.OperatingSystem}
                        className="environment-dropdown margin-bottom"
                        calloutContentClassName="environment-dropdown-callout"
                        items={this._getPlatforms(this.state.isDotnetCoreV2Agent)}
                        onSelect={this._onPlatformSelected}
                        selection={this._osSelection}
                    />
                </FormItem>
                <Link href={TargetUtils.getSystemPreReqsLinkUrl(this.state.selectedPlatform)} target="_blank" rel="external noopener noreferrer">
                    <Icon iconName="Unknown" className="system-pre-icon" />
                    <span className="system-pre-link">
                        {Resources.SystemPreRequisitesLinkText}
                    </span>
                </Link>
                <div className="use-pat-token">
                    {isHosted &&
                        <Checkbox
                            className="add-machine-scripts-pat"
                            label={Resources.PATCheckBoxLabel}
                            checked={this.state.usePATToken}
                            onChange={this._onCheckboxChange} >
                        </Checkbox>
                    }
                </div>
                <Link
                    onClick={this._onCopyScriptClick}
                    className="copy-link"
                    rel="external noopener noreferrer"
                    onFocus={this._onCopyScriptToolTipReset}
                    onMouseOver={this._onCopyScriptToolTipReset}
                    tooltipProps={{ id: "copylink-tooltip-message", text: this.state.toolTipMessage }}>
                    {copyLinkTitle}
                    <Icon iconName="Copy" className="copy-link-icon" />
                </Link>
            </div>
        );
    }

    private _getState(): IAddTargetState {
        return {
            packages: {},
            selectedPlatform: TargetUtils.ResourceTargetConstants.Windows,
            errorMessages: {},
            usePATToken: false,
            isDotnetCoreV2Agent: true,
            accessToken: "",
            toolTipMessage: Resources.CopyToClipboard,
            taskAgentPool: undefined
        };
    }

    private _getPlatforms(isDotnetCoreV2Agent: boolean): IListBoxItem[] {
        const platforms: IListBoxItem[] = [];

        if (isDotnetCoreV2Agent) {
            platforms.push({ id: TargetUtils.ResourceTargetConstants.Windows, text: Resources.AgentPlatformWindows });
            platforms.push({ id: TargetUtils.ResourceTargetConstants.Linux, text: Resources.AgentPlatformLinux });
        }
        else {
            platforms.push({ id: TargetUtils.ResourceTargetConstants.Windows_7, text: Resources.AgentPlatformWindows });
            platforms.push({ id: TargetUtils.ResourceTargetConstants.Ubuntu14_04, text: Resources.AgentPlatformUbuntu14 });
            platforms.push({ id: TargetUtils.ResourceTargetConstants.Ubuntu16_04, text: Resources.AgentPlatformUbuntu16 });
            platforms.push({ id: TargetUtils.ResourceTargetConstants.RedHat7_2, text: Resources.AgentPlatformRedhat7 });
        }

        return platforms;
    }

    private _onPlatformSelected = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem): void => {
        this.setState({
            selectedPlatform: item.id
        });
    }

    private _onCopyScriptClick = (): void => {
        const script = this._getScript(true);
        this.copyContentToClipboard(script);
        this.setState({
            toolTipMessage: Resources.Copied
        });
    }

    private _onCopyScriptToolTipReset = (): void => {
        this.setState({
            toolTipMessage: Resources.CopyToClipboard
        })
    }

    private _onCheckboxChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean): void => {
        if (isChecked && (!this.state.accessToken || this.state.accessToken.length < 1)) {
            this._vmActionCreator.getPATForDeploymentPool(this.context.pageContext, this.props.virtualMachineGroup.poolId);
        }

        this.setState({
            usePATToken: isChecked
        })
    }

    private copyContentToClipboard(data: string) {
        const textNode = document.createTextNode(data);
        const body = document.body;
        body.appendChild(textNode);

        if (window.getSelection) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(textNode);
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand('copy');
                selection.removeAllRanges();
            }
        }

        textNode.remove();
    }

    private _onGetTargetPackagesOperation = (): void => {
        this.setState({
            packages: this._vmStore.getState().targetPackages
        });
    }

    private _onGetTaskAgentPoolOperation = (): void => {
        this.setState({
            taskAgentPool: this._vmStore.getState().taskAgentPool
        });
    }

    private _onGetPATTokenOperation = (): void => {
        this.setState({
            accessToken: this._vmStore.getState().patToken
        });
    }

    private _getScript = (copyAuthParams: boolean): string => {
        const agentDownoloadUrl = this._getAgentDownloadUrl(this.state.selectedPlatform);
        if (!agentDownoloadUrl) {
            return "";
        }

        const url = PageContextHelper.getAccountUri(this.context.pageContext);
        const resourceParams = this._getResourceParams();
        const projectParams = this._getProjectParams();
        const collectionParams: string = this._getCollectionParams();
        const authParameters: string = this._getAuthParams(copyAuthParams);

        if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Ubuntu14_04)) {
            return StringUtils.format(TargetUtils.AgentConfigurationScripts.LinuxInteractive, agentDownoloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
        }
        else if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Ubuntu16_04)) {
            return StringUtils.format(TargetUtils.AgentConfigurationScripts.LinuxService, agentDownoloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
        }
        else if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.RedHat7_2)) {
            return StringUtils.format(TargetUtils.AgentConfigurationScripts.LinuxService, agentDownoloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
        }
        else if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Linux)) {
            return StringUtils.format(TargetUtils.AgentConfigurationScripts.Linux, agentDownoloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
        }
        else {
            const winConfigScript: string = StringUtils.format(TargetUtils.AgentConfigurationScripts.Windows, agentDownoloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
            const agentConfigurationPSAdminPromptCheck: string = StringUtils.format(TargetUtils.AgentConfigurationScripts.PSAdminPromptCheck, Resources.RunCommandInAdministratorPowerShellPrompt);
            const agentConfigurationPSMinVersionCheck: string = StringUtils.format(TargetUtils.AgentConfigurationScripts.PSMinVersionCheck, Resources.PSMinVersionCheck);
            return agentConfigurationPSAdminPromptCheck.concat(agentConfigurationPSMinVersionCheck).concat(winConfigScript);
        }
    }

    private _getResourceParams(): string {
        let resourceParams = "";
        if (this._isDeploymentGroupResource()) {
            resourceParams = StringUtils.format(TargetUtils.AgentConfigurationScripts.DeploymentGroupParams, this.props.resourceName);
        }
        else {
            const taskAgentPool = this.state.taskAgentPool;
            if (taskAgentPool) {
                resourceParams = StringUtils.format(TargetUtils.AgentConfigurationScripts.DeploymentPoolParams, taskAgentPool.name);
            }
        }

        return resourceParams;
    }

    private _getProjectParams(): string {
        let projectParams = "";

        if (this._isDeploymentGroupResource()) {
            const projectName = PageContextHelper.getProjectName(this.context.pageContext);
            projectParams = StringUtils.format(TargetUtils.AgentConfigurationScripts.ProjectParams, projectName);
        }

        return projectParams;
    }

    private _getAuthParams(copyAuthParams: boolean): string {
        let authParameters = "";
        const token: string = this.state.accessToken;
        const isSchemeHttp: boolean = !PageContextHelper.isSSLEnabled(this.context.pageContext);
        if (isSchemeHttp) {
            if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows) || StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows_7)) {
                authParameters = TargetUtils.AgentConfigurationScripts.AuthParamIntegrated;
            }
            else {
                authParameters = TargetUtils.AgentConfigurationScripts.AuthParamNegotiate;
            }
        }
        else if (copyAuthParams && this.state.usePATToken && token && token.length > 0) {
            authParameters = StringUtils.format(TargetUtils.AgentConfigurationScripts.AuthParameters, token);
        }

        return authParameters;
    }

    private _getCollectionParams(): string {
        let collectionName = "";
        const isOnPremise: boolean = !PageContextHelper.isHostedType(this.context.pageContext);

        if (isOnPremise && this._isDeploymentGroupResource()) {
            collectionName = StringUtils.format(TargetUtils.AgentConfigurationScripts.CollectionName, PageContextHelper.getCollectionName(this.context.pageContext));
        }

        return collectionName;
    }

    private _getRunScriptGuidance(): string {
        if (!this.props.copyScriptEnabled) {
            if (!!this.props.warningMessage) {
                return Resources.GuidanceForInsufficientPermissions;
            }
            return this._isDeploymentGroupResource() ? Resources.GuidanceForUnsavedDeploymentGroup : Resources.GuidanceForUnsavedDeploymentPool;
        }
        if (StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows) || StringUtils.equals(this.state.selectedPlatform, TargetUtils.ResourceTargetConstants.Windows_7)) {
            return Resources.GuidanceForRunPowerShellScript;
        }
        else {
            return Resources.GuidanceForRunShellScript;
        }
    }

    private _getAgentDownloadUrl(platform: string): string | undefined {
        if (!this.state.packages) {
            return undefined;
        }
        if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.Ubuntu14_04)) {
            const ubuntu14_package = this.state.packages[TargetUtils.PackageTypes.Ubuntu_14];
            if (ubuntu14_package) {
                return ubuntu14_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.Ubuntu16_04)) {
            const ubuntu16_package = this.state.packages[TargetUtils.PackageTypes.Ubuntu_16];
            if (ubuntu16_package) {
                return ubuntu16_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.RedHat7_2)) {
            const redhad7_package = this.state.packages[TargetUtils.PackageTypes.RedHat_72];
            if (redhad7_package) {
                return redhad7_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.Windows_7)) {
            const win7_package = this.state.packages[TargetUtils.PackageTypes.Windows_7];
            if (win7_package) {
                return win7_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.Windows)) {
            const win_package = this.state.packages[TargetUtils.PackageTypes.Windows];
            if (win_package) {
                return win_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, TargetUtils.ResourceTargetConstants.Linux)) {
            const linux_package = this.state.packages[TargetUtils.PackageTypes.Linux];
            if (linux_package) {
                return linux_package.downloadUrl;
            }
        }

        return undefined;
    }

    private _isDeploymentGroupResource(): boolean {
        return StringUtils.equals(this.props.resourceType, TargetUtils.AddTargetGuidanceResourceTypes.DeploymentGroup);
    }

    private _getErrorMessage = (): JSX.Element | undefined => {
        return !!this.state.errorMessage ?
            <MessageBar
                severity={MessageBarSeverity.Error}
                onDismiss={this._clearErrorMessage}>
                {this.state.errorMessage}
            </MessageBar> : undefined;
    }

    private _getWarningMessage = (): JSX.Element | undefined => {
        return this.state.showWarning ?
            <MessageBar
                severity={MessageBarSeverity.Warning}
                onDismiss={this._clearWarningMessage}>
                {this.props.warningMessage}
            </MessageBar> : undefined;
    }

    private _updateErrorMessage = (sender: any, error: any): void => {
        this.setState({
            errorMessage: getErrorMessage(error)
        });
    }

    private _clearErrorMessage = (): void => {
        this.setState({
            errorMessage: ""
        });
    }

    private _clearWarningMessage = (): void => {
        this.setState({
            showWarning: false
        });
    }
}
